// "Field captures" review tab (Asset data workspace, 2026-10-02).
// Reviewers (configure+ or the asset_data team) see every capture of their
// organisation: photos (processed copy; original on demand), where it was
// taken (road context from the server), the candidates the crew chose from,
// and three opt-in actions - set a review status, propose an asset issue
// (asset_issues, routed to asset data - never a correction), and confirm the
// failed component, which is the ONLY way a capture reaches the
// field_confirmed_failures export for the break model.
import { showToast } from "../toast";
import { openBlurEditor } from "./blurEditor";
import { fill, h, fmtDate } from "./dom";
import type { Capture, FieldConfig } from "./fieldApi";
import { fieldApi, photoUrl } from "./fieldApi";

let container: HTMLElement | null = null;
let cfg: FieldConfig | null = null;
let statusFilter = "submitted";
let focusId: number | null = null;

function label(list: { key: string | number; label: string }[] | undefined, key: string | number | null): string {
  if (key === null || key === undefined) return "—";
  return list?.find((o) => String(o.key) === String(key))?.label ?? String(key);
}

/** The card's headline. An as-built or progress capture isn't a fault, so
 * it leads with the form ("As-built record"), not the default fault type
 * ("No fault found / inspection only" undersold it; showcase 2026-10-09). */
function headline(c: Capture): string {
  const form = cfg?.forms?.[c.form_kind]?.label;
  const isFaultForm = !/as[_-]?built|progress/i.test(c.form_kind);
  if (!isFaultForm) return form ?? c.form_kind;
  return label(cfg?.fault_types, c.fault_type);
}

function card(c: Capture): HTMLElement {
  const comp = h("select", { class: "fa-input" },
    ...(cfg?.failure_components ?? []).map((o) => h("option", { value: String(o.key) }, o.label)));
  comp.value = c.confirmed_component ?? c.failure_component ?? cfg?.default_component?.[c.asset_type ?? ""] ?? "unknown";
  const note = h("input", { class: "fa-input", placeholder: "Review note (optional)" });
  const act = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      showToast(msg);
      void refresh();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed");
    }
  };
  const needsBlur = c.photos.some((p) => !p.processed && (p.error || p.redaction?.status !== "blurred"));
  return h("article", { class: "fr-card", "data-capture-id": c.id },
    h("div", { class: "fr-photos" },
      ...c.photos.map((p) =>
        p.processed
          ? h("span", { class: "fr-photo" },
              h("a", { href: photoUrl(c, p, "shared"), target: "_blank", rel: "noopener", title: "Shared copy (faces / plates blurred)" },
                h("img", { src: photoUrl(c, p, "thumb"), alt: "", loading: "lazy" })),
              h("button", { class: "fa-btn fr-blur-btn", title: "Add or remove blur regions",
                onclick: () => void openBlurEditor(c, p, () => void refresh()) },
                `Edit blur${p.redaction ? ` (${p.redaction.n_auto + p.redaction.n_manual})` : ""}`))
          : h("span", { class: "fa-noimg", title: p.error ?? "Processing (thumbnail + privacy blur)" }, p.error ? "⚠" : "⏳"))),
    h("div", { class: "fr-body" },
      h("strong", { class: "fr-title" }, `#${c.id} ${headline(c)} - ${c.asset_id ?? "no asset picked"}`),
      h("small", {}, `${fmtDate(c.captured_at ?? c.received_at)} · ${c.user_name ?? ""} · ${cfg?.forms?.[c.form_kind]?.label ?? c.form_kind} · severity ${c.severity ?? "—"} · ${c.status}`),
      c.road_context?.sentence ? h("p", {}, c.road_context.sentence) : null,
      c.lat !== null ? h("small", {}, `GPS ${c.lat?.toFixed(6)}, ${c.lon?.toFixed(6)} ±${Math.round(c.accuracy_m ?? 0)} m · heading ${c.heading_deg ?? "—"}°`) : null,
      c.notes ? h("p", {}, c.notes) : null,
      c.asset_candidates?.length ? h("small", {}, `Chosen from: ${c.asset_candidates.map((x) => `${x.asset_id} (${Math.round(x.distance_m)} m)`).join(", ")}`) : null,
      cfg?.redaction_implemented
        ? h("small", { class: "fa-muted" }, "Shared photos are EXIF-stripped with faces and number plates blurred automatically - check them with \"Edit blur\".")
        : h("small", { class: "fa-muted" }, "Photos are EXIF-stripped; faces/number plates are not yet blurred - don't share outside the team."),
      needsBlur
        ? h("span", {}, h("small", { class: "fa-muted" }, "Some photos are not blurred yet, so they are not shared. "),
            h("button", { class: "fa-btn", onclick: () => void act(() => fieldApi.reprocess(c.id), "Blur job queued") }, "Re-run blur"))
        : null,
      c.asset_issue_id ? h("small", {}, `Asset issue #${c.asset_issue_id} raised.`) : null,
      c.confirmed_component ? h("small", {}, `Confirmed component: ${label(cfg?.failure_components, c.confirmed_component)}`) : null,
      h("div", { class: "fr-actions" },
        note,
        h("button", { class: "fa-btn", onclick: () => void act(() => fieldApi.review(c.id, "reviewed", note.value), "Marked reviewed") }, "Reviewed"),
        h("button", { class: "fa-btn", onclick: () => void act(() => fieldApi.review(c.id, "actioned", note.value), "Marked actioned") }, "Actioned"),
        h("button", { class: "fa-btn", onclick: () => void act(() => fieldApi.review(c.id, "rejected", note.value), "Rejected") }, "Reject"),
        c.asset_id && !c.asset_issue_id
          ? h("button", { class: "fa-btn", onclick: () => void act(() => fieldApi.proposeIssue(c.id, note.value || undefined), "Asset issue proposed") }, "Propose asset issue")
          : null,
        c.asset_id
          ? h("span", { class: "fr-confirm" }, comp,
              h("button", { class: "fa-btn fa-primary", onclick: () => void act(() => fieldApi.confirmFailure(c.id, comp.value, note.value || undefined), "Failure confirmed - export updated") }, "Confirm failure"))
          : null,
      )),
  );
}

async function refresh(): Promise<void> {
  if (!container) return;
  const list = h("div", { class: "fr-list" }, h("p", { class: "caption" }, "Loading…"));
  const filter = h("select", {},
    ...["submitted", "reviewed", "actioned", "closed", "rejected"].map((s) => h("option", { value: s }, s)));
  filter.value = statusFilter;
  filter.addEventListener("change", () => {
    statusFilter = filter.value;
    void refresh();
  });
  fill(container, h("h2", {}, "Field captures"), h("label", {}, "Status ", filter), list);
  try {
    cfg = cfg ?? (await fieldApi.config());
    const caps = await fieldApi.reviewQueue(statusFilter);
    fill(list, ...(caps.length ? caps.map(card) : [h("p", { class: "caption" }, "Nothing here.")]));
    if (focusId !== null) {
      const target = list.querySelector<HTMLElement>(`[data-capture-id="${focusId}"]`);
      focusId = null;
      if (target) {
        target.classList.add("fr-focus");
        target.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }
  } catch {
    fill(list, h("p", { class: "caption" }, "Field captures need the 'configure' role or the Asset data team."));
  }
}

/** Open the review tab on one capture (from an asset's "Field photos",
 * 2026-10-09): switches the status filter to the capture's status and
 * scrolls its card into view. The caller makes sure the tab is shown. */
export function focusCaptureInReview(captureId: number, status: string): void {
  statusFilter = status;
  focusId = captureId;
  const btn = document.querySelector<HTMLButtonElement>('#sidebar .tab-btn[data-tab="fieldReview"]');
  if (btn) btn.click();
  else void refresh();
}

export function initFieldReview(): void {
  container = document.getElementById("field-review-container");
  document.querySelector<HTMLButtonElement>('#sidebar .tab-btn[data-tab="fieldReview"]')?.addEventListener("click", () => void refresh());
}
